import React from 'react';
import AdminPage from 'lib/admin-kit/components/AdminPage';
import AdminTable from 'lib/admin-kit/components/AdminTable';
import PeriodSelector from './PeriodSelector';
import GroupBySelector from './GroupBySelector';
import SearchBar, { USER_FIELD_OPTIONS } from './SearchBar';
import { formatCost, formatCount } from './format';

const ENDPOINT = '/internal_admin/admin_kit/ai_usage_logs';

const dealerLinkCell = (row) => {
  if (!row.dealer_id) return '—';
  return (
    <a
      href={`${ENDPOINT}/${row.dealer_id}`}
      style={{ textDecoration: 'underline' }}
    >
      {row.dealer_id}
    </a>
  );
};

const dealerNameCell = (row) => {
  if (!row.dealer_id) return row.dealer_name ?? '—';
  return (
    <a
      href={`${ENDPOINT}/${row.dealer_id}`}
      style={{ textDecoration: 'underline' }}
    >
      {row.dealer_name ?? '—'}
    </a>
  );
};

const userCell = (row) => {
  if (!row.user_id) return row.user_email ?? '—';
  return (
    <a
      href={`${ENDPOINT}/users/${row.user_id}`}
      style={{ textDecoration: 'underline' }}
    >
      {row.user_email ?? row.user_id}
    </a>
  );
};

const DEALER_COLUMNS = [
  {
    name: 'dealer_id',
    title: 'Dealer ID',
    DataCell: ({ rowData }) => dealerLinkCell(rowData),
  },
  {
    name: 'dealer_name',
    title: 'Dealer Name',
    DataCell: ({ rowData }) => dealerNameCell(rowData),
  },
  {
    name: 'count',
    title: 'Calls',
    DataCell: ({ rowData }) => formatCount(rowData.count),
  },
  {
    name: 'input_tokens',
    title: 'Input Tokens',
    DataCell: ({ rowData }) => formatCount(rowData.input_tokens),
  },
  {
    name: 'output_tokens',
    title: 'Output Tokens',
    DataCell: ({ rowData }) => formatCount(rowData.output_tokens),
  },
  {
    name: 'cost_usd',
    title: 'Cost',
    DataCell: ({ rowData }) => formatCost(rowData.cost_usd),
  },
];

const USER_COLUMNS = [
  {
    name: 'user_email',
    title: 'User',
    DataCell: ({ rowData }) => userCell(rowData),
  },
  {
    name: 'count',
    title: 'Calls',
    DataCell: ({ rowData }) => formatCount(rowData.count),
  },
  {
    name: 'input_tokens',
    title: 'Input Tokens',
    DataCell: ({ rowData }) => formatCount(rowData.input_tokens),
  },
  {
    name: 'output_tokens',
    title: 'Output Tokens',
    DataCell: ({ rowData }) => formatCount(rowData.output_tokens),
  },
  {
    name: 'cost_usd',
    title: 'Cost',
    DataCell: ({ rowData }) => formatCost(rowData.cost_usd),
  },
];

const Index = ({
  rows = [],
  totals = {},
  period = 'day',
  q = '',
  searchField = 'dealer_id',
  groupBy = 'dealer',
}) => {
  const isUserView = groupBy === 'user';
  const columns = isUserView ? USER_COLUMNS : DEALER_COLUMNS;

  return (
    <AdminPage title="AI Usage" breadcrumbs={[{ text: 'AI Usage' }]}>
      <GroupBySelector groupBy={groupBy} basePath={ENDPOINT} period={period} />
      <SearchBar
        initialField={searchField}
        initialValue={q}
        fieldOptions={isUserView ? USER_FIELD_OPTIONS : undefined}
        onSearch={(field, text) => {
          const params = new URLSearchParams({
            period,
            group_by: groupBy,
            search_field: field,
          });
          if (text) params.set('q', text);
          window.location.href = `${ENDPOINT}?${params.toString()}`;
        }}
      />
      <PeriodSelector
        period={period}
        basePath={ENDPOINT}
        q={q}
        searchField={searchField}
        groupBy={groupBy}
      />

      <AdminTable
        columns={columns}
        data={rows}
        getRowKey={(r) => (isUserView ? r.user_id ?? 'no-user' : r.dealer_id)}
        emptyText="No AI usage recorded for this period."
        toolbar={() => (
          <div style={{ fontSize: 13, color: '#555', padding: '4px 0' }}>
            Total: {formatCount(totals.count)} calls,{' '}
            {formatCount(totals.input_tokens)} input tokens,{' '}
            {formatCount(totals.output_tokens)} output tokens,{' '}
            {formatCost(totals.cost_usd)}
          </div>
        )}
      />
    </AdminPage>
  );
};

export default Index;
